import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormGroupDirective,
} from '@angular/forms';
import {
  AsYouType,
  CountryCode,
  isSupportedCountry,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { Subscription } from 'rxjs';
import { ValidatorMessageModel } from 'src/app/components/form/form-helpers/validation/validator-message.model';
import { TranslateService } from '@ngx-translate/core';
import { countries, CountryOption } from '../../../shared/countries';

type ParsedPhoneNumber = NonNullable<
  ReturnType<typeof parsePhoneNumberFromString>
>;

type ParseCandidate = {
  value: string;
  countryCode?: CountryCode;
};

type PhoneCountryOption = CountryOption & {
  label: string;
  searchText: string;
};

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class PhoneInputComponent implements OnInit, OnDestroy {
  @Input() controlName?: string;
  @Input() initialPhoneNumber: string = '';
  @Input() readonly: boolean = false;
  @Input() valid?: boolean;

  @Output() phoneNumberChange = new EventEmitter<string>();

  countryOptions = this.createFallbackCountryOptions();
  selectedCountry: PhoneCountryOption =
    this.countryOptions.find((country) => country.iso === 'CH') ??
    this.countryOptions[0];
  phoneNumber: string = '';
  formattedNumber: string = '';
  errorMessage: string = '';
  isValid: boolean = false;

  private controlSub?: Subscription;
  private languageSub?: Subscription;
  private isWritingControl = false;

  constructor(
    private formGroupDirective: FormGroupDirective,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.refreshCountryTranslations();

    this.languageSub = this.translate.onLangChange.subscribe(() => {
      this.refreshCountryTranslations();
    });

    this.controlSub = this.control?.valueChanges.subscribe((value) => {
      if (!this.isWritingControl) {
        this.applyExternalValue(value);
      }
    });

    this.applyExternalValue(this.initialPhoneNumber || this.control?.value);
  }

  ngOnDestroy(): void {
    this.controlSub?.unsubscribe();
    this.languageSub?.unsubscribe();
  }

  get control(): AbstractControl | null {
    return this.controlName
      ? this.formGroupDirective.form.get(this.controlName)
      : null;
  }

  onCountryChange(country?: PhoneCountryOption) {
    if (country) {
      this.selectedCountry = country;
    }

    this.syncFromInput(this.phoneNumber);
  }

  onPhoneNumberInput(value: string): void {
    this.syncFromInput(value);
  }

  compareCountries = (
    firstCountry?: PhoneCountryOption,
    secondCountry?: PhoneCountryOption,
  ): boolean => firstCountry?.iso === secondCountry?.iso;

  searchCountry = (term: string, country: PhoneCountryOption): boolean => {
    const normalizedTerm = this.normalizeSearchTerm(term);

    if (!normalizedTerm) {
      return true;
    }

    return country.searchText.includes(normalizedTerm);
  };

  onInputBlur() {
    this.control?.markAsTouched();

    const parsed = this.parsePhoneNumber(this.phoneNumber);
    if (parsed?.isValid()) {
      this.setValidPhoneNumber(parsed, true, false);
    }
  }

  private applyExternalValue(value: unknown): void {
    const rawValue = typeof value === 'string' ? value : '';

    if (!rawValue.trim()) {
      this.clearPhoneNumber(false);
      return;
    }

    this.syncFromInput(rawValue, false, false);
  }

  private syncFromInput(
    value: string,
    markDirty = true,
    emitChange = true,
  ): void {
    const sanitizedValue = this.sanitizeInput(value);

    if (!sanitizedValue.trim()) {
      this.clearPhoneNumber(markDirty);
      return;
    }

    const parsed = this.parsePhoneNumber(sanitizedValue);

    if (parsed?.isValid()) {
      this.setValidPhoneNumber(parsed, markDirty, emitChange);
      return;
    }

    this.phoneNumber = this.formatPartialPhoneNumber(sanitizedValue);
    this.formattedNumber = '';
    this.isValid = false;
    this.errorMessage = 'validators.phone.invalidPhone';
    this.writeControlValue(this.phoneNumber, markDirty);
    this.setInvalidPhoneError();
  }

  private setValidPhoneNumber(
    parsed: ParsedPhoneNumber,
    markDirty = true,
    emitChange = true,
  ): void {
    this.selectCountryForParsedNumber(parsed);
    this.phoneNumber = parsed.formatNational();
    this.formattedNumber = parsed.formatInternational();
    this.isValid = true;
    this.errorMessage = '';
    this.writeControlValue(this.formattedNumber, markDirty);
    if (emitChange) {
      this.phoneNumberChange.emit(this.formattedNumber);
    }
  }

  private clearPhoneNumber(markDirty: boolean): void {
    this.phoneNumber = '';
    this.formattedNumber = '';
    this.isValid = false;
    this.errorMessage = '';
    this.writeControlValue(null, markDirty);
  }

  private parsePhoneNumber(rawValue: string): ParsedPhoneNumber | null {
    const candidates = this.getParseCandidates(rawValue);
    let firstParsed: ParsedPhoneNumber | null = null;

    for (const candidate of candidates) {
      const parsed = parsePhoneNumberFromString(
        candidate.value,
        candidate.countryCode,
      );

      if (!parsed) {
        continue;
      }

      firstParsed = firstParsed ?? parsed;

      if (parsed.isValid()) {
        return parsed;
      }
    }

    return firstParsed;
  }

  private getParseCandidates(rawValue: string): ParseCandidate[] {
    const value = rawValue.trim();
    const digits = value.replace(/\D/g, '');
    const countryCode = this.getCountryCode(this.selectedCountry);
    const candidates = new Array<ParseCandidate>();

    if (value.startsWith('+')) {
      candidates.push({ value });
    }

    if (digits.startsWith('00') && digits.length > 2) {
      candidates.push({ value: `+${digits.slice(2)}` });
    }

    if (digits.startsWith('011') && digits.length > 3) {
      candidates.push({ value: `+${digits.slice(3)}` });
    }

    const detectedDialCountry = this.findCountryByDialDigits(digits);
    if (detectedDialCountry) {
      candidates.push({ value: `+${digits}` });
    }

    if (countryCode) {
      candidates.push({ value, countryCode });
    }

    candidates.push({ value });

    return this.dedupeCandidates(candidates);
  }

  private formatPartialPhoneNumber(value: string): string {
    const countryCode = this.getCountryCode(this.selectedCountry);
    return new AsYouType(countryCode).input(value);
  }

  private selectCountryForParsedNumber(parsed: ParsedPhoneNumber): void {
    if (!parsed.country) {
      return;
    }

    const country = this.countryOptions.find(
      (item) => item.iso === parsed.country,
    );

    if (country) {
      this.selectedCountry = country;
    }
  }

  private findCountryByDialDigits(digits: string): CountryOption | undefined {
    return [...this.countryOptions]
      .sort((a, b) => b.dial.length - a.dial.length)
      .find((country) => {
        const dialDigits = country.dial.replace(/\D/g, '');
        return (
          digits.startsWith(dialDigits) && digits.length > dialDigits.length
        );
      });
  }

  private sanitizeInput(value: string): string {
    const cleanedValue = value
      .replace(/[^\d+\s().-]/g, '')
      .replace(/\s+/g, ' ');
    const plusIndex = cleanedValue.indexOf('+');

    if (plusIndex === -1) {
      return cleanedValue.trimStart();
    }

    return (
      cleanedValue.slice(0, plusIndex + 1) +
      cleanedValue.slice(plusIndex + 1).replace(/\+/g, '')
    ).trimStart();
  }

  private writeControlValue(value: string | null, markDirty: boolean): void {
    const control = this.control;

    if (!control) {
      return;
    }

    this.isWritingControl = true;
    try {
      control.setValue(value, { emitEvent: false });
      control.updateValueAndValidity({ emitEvent: false });

      if (markDirty) {
        control.markAsDirty();
      }
    } finally {
      this.isWritingControl = false;
    }
  }

  private setInvalidPhoneError(): void {
    const control = this.control;

    if (!control) {
      return;
    }

    control.setErrors({
      ...(control.errors ?? {}),
      invalidPhone: new ValidatorMessageModel(
        'validators.phone.invalidPhone',
      ),
    });
  }

  private getCountryCode(country: CountryOption): CountryCode | undefined {
    return isSupportedCountry(country.iso)
      ? (country.iso as CountryCode)
      : undefined;
  }

  private refreshCountryTranslations(): void {
    const selectedCountryIso = this.selectedCountry.iso;

    this.countryOptions = countries.map((country) =>
      this.createTranslatedCountryOption(country),
    );
    this.selectedCountry =
      this.countryOptions.find((country) => country.iso === selectedCountryIso) ??
      this.countryOptions[0];
  }

  private createFallbackCountryOptions(): PhoneCountryOption[] {
    return countries.map((country) => ({
      ...country,
      label: country.name,
      searchText: this.normalizeSearchTerm(
        `${country.name} ${country.iso} ${country.dial}`,
      ),
    }));
  }

  private createTranslatedCountryOption(
    country: CountryOption,
  ): PhoneCountryOption {
    const translationKey = `strict.countryCode.${country.iso}`;
    const translatedName = this.translate.instant(translationKey);
    const label =
      translatedName === translationKey ? country.name : translatedName;

    return {
      ...country,
      label,
      searchText: this.normalizeSearchTerm(
        `${label} ${country.name} ${country.iso} ${country.dial}`,
      ),
    };
  }

  private normalizeSearchTerm(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private dedupeCandidates(candidates: ParseCandidate[]): ParseCandidate[] {
    return candidates.filter((candidate, index) => {
      const key = `${candidate.value}-${candidate.countryCode ?? ''}`;
      return (
        candidates.findIndex(
          (item) => `${item.value}-${item.countryCode ?? ''}` === key,
        ) === index
      );
    });
  }
}
